import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FORM_SELECTOR =
    '[data-map-suggestion-form]';

function parseCoordinate(value) {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function parseJsonArray(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        return Array.isArray(parsed)
            ? parsed
            : [];
    } catch {
        return [];
    }
}

function initializeForm(form) {
    if (
        form.dataset
            .mapSuggestionJunctionReady
        === 'true'
    ) {
        return;
    }

    const typeSelect = form.querySelector(
        '[data-map-suggestion-type]'
    );

    const section = form.querySelector(
        '[data-map-suggestion-junction-section]'
    );

    const picker = form.querySelector(
        '[data-map-suggestion-junction-picker]'
    );

    const mapElement = form.querySelector(
        '[data-map-suggestion-junction-map]'
    );

    const status = form.querySelector(
        '[data-map-suggestion-junction-status]'
    );

    const latitudeInput = form.querySelector(
        '[data-landmark-latitude]'
    );

    const longitudeInput = form.querySelector(
        '[data-landmark-longitude]'
    );

    if (
        !typeSelect
        || !section
        || !picker
        || !mapElement
        || !latitudeInput
        || !longitudeInput
    ) {
        return;
    }

    form.dataset
        .mapSuggestionJunctionReady =
            'true';

    const existingPaths =
        parseJsonArray(
            picker.dataset.existingPaths
        );

    const existingJunctions =
        parseJsonArray(
            picker.dataset.existingJunctions
        );

    const SNAP_DISTANCE_PX = 18;

    let map = null;
    let marker = null;
    let syncingInputs = false;

    const setStatus = (
        message
    ) => {
        if (status) {
            status.textContent = message;
        }
    };

    const syncInputs = (
        latitude,
        longitude
    ) => {
        syncingInputs = true;

        latitudeInput.value =
            latitude.toFixed(7);
        longitudeInput.value =
            longitude.toFixed(7);

        latitudeInput.dispatchEvent(
            new Event(
                'input',
                {
                    bubbles: true,
                }
            )
        );

        longitudeInput.dispatchEvent(
            new Event(
                'input',
                {
                    bubbles: true,
                }
            )
        );

        syncingInputs = false;
    };

    const placeMarker = (
        latitude,
        longitude,
        center = false
    ) => {
        if (!map) {
            return;
        }

        const point =
            L.latLng(
                latitude,
                longitude
            );

        if (!marker) {
            marker = L.circleMarker(
                point,
                {
                    radius: 9,
                    color: '#ffffff',
                    weight: 3,
                    fillColor: '#7c3aed',
                    fillOpacity: 1,
                }
            ).addTo(map);
        } else {
            marker.setLatLng(point);
        }

        marker.bringToFront();

        if (center) {
            map.setView(
                point,
                Math.max(
                    map.getZoom(),
                    17
                ),
                {
                    animate: false,
                }
            );
        }
    };

    const nearestPointOnSegment = (
        clickPoint,
        startPoint,
        endPoint
    ) => {
        const dx =
            endPoint.x - startPoint.x;

        const dy =
            endPoint.y - startPoint.y;

        const lengthSquared =
            dx * dx + dy * dy;

        if (lengthSquared === 0) {
            return startPoint;
        }

        const t = Math.max(
            0,
            Math.min(
                1,
                (
                    (
                        clickPoint.x
                        - startPoint.x
                    ) * dx
                    + (
                        clickPoint.y
                        - startPoint.y
                    ) * dy
                ) / lengthSquared
            )
        );

        return L.point(
            startPoint.x + t * dx,
            startPoint.y + t * dy
        );
    };

    const nearestPathPoint = (
        latlng
    ) => {
        if (!map) {
            return null;
        }

        const clickPoint =
            map.latLngToContainerPoint(
                latlng
            );

        let nearest = null;
        let nearestDistance =
            Number.POSITIVE_INFINITY;

        existingPaths.forEach(
            (path) => {
                const pathPoints =
                    Array.isArray(
                        path.path_points
                    )
                        ? path.path_points
                            .map((point) => {
                                const latitude =
                                    parseCoordinate(
                                        point?.latitude
                                    );

                                const longitude =
                                    parseCoordinate(
                                        point?.longitude
                                    );

                                return (
                                    latitude !== null
                                    && longitude !== null
                                )
                                    ? L.latLng(
                                        latitude,
                                        longitude
                                    )
                                    : null;
                            })
                            .filter(Boolean)
                        : [];

                for (
                    let index = 0;
                    index
                        < pathPoints.length - 1;
                    index += 1
                ) {
                    const startPoint =
                        map.latLngToContainerPoint(
                            pathPoints[index]
                        );

                    const endPoint =
                        map.latLngToContainerPoint(
                            pathPoints[index + 1]
                        );

                    const candidatePoint =
                        nearestPointOnSegment(
                            clickPoint,
                            startPoint,
                            endPoint
                        );

                    const distance =
                        clickPoint.distanceTo(
                            candidatePoint
                        );

                    if (
                        distance
                        < nearestDistance
                    ) {
                        const candidateLatLng =
                            map.containerPointToLatLng(
                                candidatePoint
                            );

                        nearestDistance =
                            distance;

                        nearest = {
                            path,
                            latitude:
                                candidateLatLng.lat,
                            longitude:
                                candidateLatLng.lng,
                        };
                    }
                }
            }
        );

        return (
            nearest
            && nearestDistance
                <= SNAP_DISTANCE_PX
        )
            ? nearest
            : null;
    };

    const ensureMap = () => {
        if (map) {
            window.setTimeout(
                () => map.invalidateSize(),
                50
            );

            return;
        }

        const savedLatitude =
            parseCoordinate(
                latitudeInput.value
            );

        const savedLongitude =
            parseCoordinate(
                longitudeInput.value
            );

        const centerLatitude =
            parseCoordinate(
                picker.dataset.centerLatitude
            );

        const centerLongitude =
            parseCoordinate(
                picker.dataset.centerLongitude
            );

        const initialLatitude =
            savedLatitude
            ?? centerLatitude
            ?? 0;

        const initialLongitude =
            savedLongitude
            ?? centerLongitude
            ?? 0;

        map = L.map(
            mapElement,
            {
                zoomControl: true,
                scrollWheelZoom: false,
            }
        ).setView(
            [
                initialLatitude,
                initialLongitude,
            ],
            (
                savedLatitude !== null
                || centerLatitude !== null
            )
                ? 17
                : 2
        );

        L.tileLayer(
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            {
                maxZoom: 20,
                attribution:
                    '&copy; OpenStreetMap contributors',
            }
        ).addTo(map);

        const contextLayer =
            L.layerGroup().addTo(map);

        const pathLayers =
            new Map();

        existingPaths.forEach(
            (path) => {
                const pathPoints =
                    Array.isArray(
                        path.path_points
                    )
                        ? path.path_points
                            .map((point) => {
                                const latitude =
                                    parseCoordinate(
                                        point?.latitude
                                    );

                                const longitude =
                                    parseCoordinate(
                                        point?.longitude
                                    );

                                return (
                                    latitude !== null
                                    && longitude !== null
                                )
                                    ? [
                                        latitude,
                                        longitude,
                                    ]
                                    : null;
                            })
                            .filter(Boolean)
                        : [];

                if (pathPoints.length < 2) {
                    return;
                }

                const layer =
                    L.polyline(
                        pathPoints,
                        {
                            color: '#64748b',
                            weight: 4,
                            opacity: 0.7,
                        }
                    )
                        .addTo(
                            contextLayer
                        )
                        .bindTooltip(
                            `${path.name || 'Official path'}${
                                path.type
                                    ? ` · ${path.type}`
                                    : ''
                            }`,
                            {
                                sticky: true,
                            }
                        );

                pathLayers.set(
                    Number(path.id),
                    layer
                );
            }
        );

        existingJunctions.forEach(
            (junction) => {
                const latitude =
                    parseCoordinate(
                        junction.latitude
                    );

                const longitude =
                    parseCoordinate(
                        junction.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return;
                }

                L.circleMarker(
                    [
                        latitude,
                        longitude,
                    ],
                    {
                        radius: 7,
                        color: '#ffffff',
                        weight: 2,
                        fillColor: '#4f46e5',
                        fillOpacity: 0.95,
                    }
                )
                    .addTo(contextLayer)
                    .bindTooltip(
                        `${junction.name || 'Junction'}${
                            junction.type
                                ? ` · ${junction.type}`
                                : ''
                        }`,
                        {
                            direction: 'top',
                        }
                    );
            }
        );

        let highlightedPathId = null;

        const clearPathHighlight = () => {
            if (
                highlightedPathId
                === null
            ) {
                return;
            }

            pathLayers
                .get(
                    highlightedPathId
                )
                ?.setStyle({
                    color: '#64748b',
                    weight: 4,
                    opacity: 0.7,
                });

            highlightedPathId = null;
        };

        const highlightPath = (
            path
        ) => {
            const pathId =
                Number(path?.id);

            if (
                !Number.isFinite(pathId)
            ) {
                clearPathHighlight();
                return;
            }

            if (
                highlightedPathId
                === pathId
            ) {
                return;
            }

            clearPathHighlight();

            pathLayers
                .get(pathId)
                ?.setStyle({
                    color: '#f59e0b',
                    weight: 6,
                    opacity: 1,
                });

            highlightedPathId =
                pathId;
        };

        map.on(
            'mousemove',
            (event) => {
                const snap =
                    nearestPathPoint(
                        event.latlng
                    );

                if (snap) {
                    highlightPath(
                        snap.path
                    );
                } else {
                    clearPathHighlight();
                }
            }
        );

        map.on(
            'mouseout',
            clearPathHighlight
        );

        map.on(
            'click',
            (event) => {
                const snap =
                    nearestPathPoint(
                        event.latlng
                    );

                const latitude =
                    snap
                        ? snap.latitude
                        : event.latlng.lat;

                const longitude =
                    snap
                        ? snap.longitude
                        : event.latlng.lng;

                placeMarker(
                    latitude,
                    longitude
                );

                syncInputs(
                    latitude,
                    longitude
                );

                if (snap) {
                    setStatus(
                        `Snapped to official path: ${
                            snap.path.name
                            || 'Path'
                        }.`
                    );
                } else {
                    setStatus(
                        `Selected point: ${latitude.toFixed(7)}, ${longitude.toFixed(7)}`
                    );
                }
            }
        );

        if (
            savedLatitude !== null
            && savedLongitude !== null
        ) {
            placeMarker(
                savedLatitude,
                savedLongitude,
                true
            );
        }

        window.setTimeout(
            () => map.invalidateSize(),
            100
        );
    };

    const syncFromInputs = () => {
        if (
            syncingInputs
            || !map
        ) {
            return;
        }

        const latitude =
            parseCoordinate(
                latitudeInput.value
            );

        const longitude =
            parseCoordinate(
                longitudeInput.value
            );

        if (
            latitude === null
            || longitude === null
        ) {
            return;
        }

        placeMarker(
            latitude,
            longitude,
            true
        );

        setStatus(
            `Selected point: ${latitude.toFixed(7)}, ${longitude.toFixed(7)}`
        );
    };

    const updateVisibility = () => {
        const visible =
            typeSelect.value
            === 'new_junction';

        section.classList.toggle(
            'hidden',
            !visible
        );

        if (visible) {
            ensureMap();
            syncFromInputs();
        }
    };

    latitudeInput.addEventListener(
        'input',
        syncFromInputs
    );

    longitudeInput.addEventListener(
        'input',
        syncFromInputs
    );

    typeSelect.addEventListener(
        'change',
        updateVisibility
    );

    updateVisibility();
}

function initializeForms() {
    document
        .querySelectorAll(
            FORM_SELECTOR
        )
        .forEach(
            initializeForm
        );
}

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeForms,
        {
            once: true,
        }
    );
} else {
    initializeForms();
}

document.addEventListener(
    'livewire:navigated',
    initializeForms
);
